import {
	ApplicationComponent,
	ApplicationComponentType,
	Page,
	PageComponent,
	TestStatus,
} from '@asta/core'
import { doesElementMatchSelector, Selector } from '@asta/selectors'

import { parseSelectorStrings } from './selector-parser'

//TODO: These are duplicates of the ones in the repository, fix this
export interface ExecutionStatus {
	colNum: number
	lineNum: number
	offset: number
}

export const RunWorkQueueItemState = {
	Todo: 'Todo',
	Doing: 'Doing',
	Failed: 'Failed',
	Success: 'Success',
	Partial: 'Partial',
} as const

export type RunWorkQueueItemType =
	(typeof RunWorkQueueItemType)[keyof typeof RunWorkQueueItemType]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RunWorkQueueItemType = {
	Flow: 'Flow',
	ChatGPT: 'ChatGPT',
	Component: 'Component',
	Jump: 'Jump',
	Link_Testing: 'Link Testing',
	Page: 'Page',
	Form_Field: 'Form Field',
} as const

export interface ComponentRunWorkQueueItemItem {
	label: string
	pageUrl: string
}

export type RunWorkQueueItemState =
	(typeof RunWorkQueueItemState)[keyof typeof RunWorkQueueItemState]

export interface RunWorkQueueItem {
	activityId?: string
	componentId?: string
	depth?: number
	executionStatus?: ExecutionStatus
	id: string
	item?: ComponentRunWorkQueueItemItem
	label: string
	pageId?: string
	state: RunWorkQueueItemState
	subItems?: RunWorkQueueItem[]
	type: RunWorkQueueItemType
	url?: string
}

export type DeepReadonly<T> = {
	readonly [K in keyof T]: T[K] extends object ? DeepReadonly<T[K]> : T[K]
}

// Type for analytics fetcher function
export type ApplicationAnalyticsFetcher = () => Promise<
	Record<string, { lastTested: Date; lastResult: 'passed' | 'failed' }>
>

// Priority field type
export type PriorityField =
	| 'lastTested'
	| 'lastResult'
	| 'shallowest'
	| 'deepest'

// Test history interface
export interface TestHistory {
	lastTested?: Date
	lastResult?: 'passed' | 'failed'
}

/**
 * Options for WorkQueue constructor
 */
export interface WorkQueueOptions {
	/**
	 * Array of component types to skip
	 */
	itemsToSkip?: string[]
	/**
	 * Whether to use priority-based queueing
	 */
	usePriority?: boolean
	/**
	 * Function to fetch application analytics data (required if usePriority is true)
	 */
	applicationAnalyticsFetcher?: ApplicationAnalyticsFetcher
	/**
	 * Field to prioritize by ('lastTested' or 'lastResult')
	 */
	priorityField?: PriorityField
	/**
	 * Whether to use fast testing for tables
	 */
	fastTestTables?: boolean
	/**
	 * Array of selector strings to skip. Components matching any of these selectors will be skipped.
	 *
	 * Selector formats supported:
	 * - Semantic selectors: ~"Google" or "Google"
	 * - Class selectors: ~.submit-button
	 * - Component selectors: ~#login-form
	 * - Hierarchical selectors: ~button/~span
	 * - Attribute selectors: ~[data-testid="submit"]
	 *
	 * Example: ["~\"Google\"", "~.submit-button", "~#login-form", "~button/~span", "~[data-testid=\"submit\"]"]
	 */
	selectorStringsToSkip?: string[]
	/**
	 * Whether to only enqueue link components for testing (skips forms, buttons, etc.)
	 * Pages are still enqueued to discover links within them.
	 */
	onlyTestLinks?: boolean
	/**
	 * Callback function invoked when a component is skipped.
	 * @param component The component that was skipped
	 * @param reason The reason for skipping: 'selector' if it matched a selector, 'type' if it was in itemsToSkip
	 * @param matchingSelectorString The selector string that matched (only provided if reason is 'selector')
	 * @param page The page context where the component was found (if available)
	 */
	onComponentSkipped?: (
		component: PageComponent,
		reason: 'selector' | 'type',
		matchingSelectorString?: string,
		page?: Page
	) => void
	/**
	 * Worker ID for logging purposes in parallel mode.
	 * When set, WorkQueue logs will include this ID to help trace which worker
	 * is processing which items.
	 */
	workerId?: number
}

/**
 * Unified WorkQueue class that supports both standard and priority-based queueing
 */
export class WorkQueue {
	private queue: RunWorkQueueItem[] = []
	private doingIdx: number = -1
	private itemsToSkip: string[] = []
	private fastTestTables: boolean = false
	private processedTableIds: Set<string> = new Set()
	private selectorsToSkip: Selector[] = [] // Array of parsed selectors to skip
	private selectorStringsToSkip: string[] = [] // Original selector strings for logging
	private onlyTestLinks: boolean = false
	private onComponentSkipped?: (
		component: PageComponent,
		reason: 'selector' | 'type',
		matchingSelectorString?: string,
		page?: Page
	) => void
	private loggedSkippedComponents: Set<string> = new Set() // Track logged components to avoid duplicates

	// Priority queue properties
	private usePriority: boolean = false
	private priorityField?: PriorityField
	private testHistory: Map<string, TestHistory> = new Map()
	private applicationAnalyticsFetcher?: ApplicationAnalyticsFetcher
	private lastEnqueuedPage: Page | undefined
	private itemDepths: Map<string, number> = new Map() // Cache for item depths
	private workerId?: number // Worker ID for logging in parallel mode

	/**
	 * Create a new WorkQueue instance
	 * @param options Configuration options
	 * - itemsToSkip: Array of component types to skip
	 * - usePriority: Whether to use priority-based queueing
	 * - applicationAnalyticsFetcher: Function to fetch application analytics data (required if usePriority is true)
	 * - priorityField: Field to prioritize by ('lastTested' or 'lastResult')
	 * - fastTestTables: Whether to use fast testing for tables
	 * - selectorStringsToSkip: Array of selector strings to skip. Components matching any of these selectors will be skipped.
	 *   See the WorkQueueOptions interface for details on supported selector formats.
	 * - onlyTestLinks: Whether to only enqueue link components for testing (skips forms, buttons, etc.)
	 */
	constructor(options: WorkQueueOptions = {}) {
		const {
			itemsToSkip = [],
			usePriority = false,
			applicationAnalyticsFetcher,
			priorityField = 'lastTested',
			fastTestTables = false,
			selectorStringsToSkip = [],
			onlyTestLinks = false,
			onComponentSkipped,
			workerId,
		} = options

		this.itemsToSkip = itemsToSkip
		this.usePriority = usePriority
		this.fastTestTables = fastTestTables
		this.onlyTestLinks = onlyTestLinks
		this.onComponentSkipped = onComponentSkipped
		this.workerId = workerId

		// Store original selector strings for logging purposes
		this.selectorStringsToSkip = selectorStringsToSkip

		// Parse the selector strings into Selector objects using the selector parser
		this.selectorsToSkip = parseSelectorStrings(selectorStringsToSkip)

		if (usePriority) {
			if (!applicationAnalyticsFetcher) {
				throw new Error(
					'applicationAnalyticsFetcher is required when usePriority is true'
				)
			}

			this.applicationAnalyticsFetcher = applicationAnalyticsFetcher
			this.priorityField = priorityField
			this.populateTestHistory()
		}
	}

	/**
	 * Fetches and populates the test history data for priority queueing
	 */
	private async populateTestHistory(): Promise<void> {
		if (!this.applicationAnalyticsFetcher || !this.usePriority) return

		const analytics = await this.applicationAnalyticsFetcher()
		for (const [id, history] of Object.entries(analytics)) {
			this.testHistory.set(id, history)
		}
	}

	private _filterFn: (
		item: RunWorkQueueItem | PageComponent | Page
	) => boolean = () => true

	private _sortFn: (a: RunWorkQueueItem, b: RunWorkQueueItem) => number =
		() => 0

	public set filter(
		fn: (item: RunWorkQueueItem | PageComponent | Page) => boolean
	) {
		this._filterFn = fn
	}

	public set sort(fn: (a: RunWorkQueueItem, b: RunWorkQueueItem) => number) {
		this._sortFn = fn
	}

	/**
	 * Sets the callback function to be invoked when a component is skipped
	 * @param callback The callback function
	 */
	public setOnComponentSkipped(
		callback: (
			component: PageComponent,
			reason: 'selector' | 'type',
			matchingSelectorString?: string,
			page?: Page
		) => void
	): void {
		this.onComponentSkipped = callback
	}

	public get length(): number {
		return this.queue.length - (this.doingIdx < 0 ? 0 : this.doingIdx)
	}

	public get items(): DeepReadonly<RunWorkQueueItem>[] {
		return this.queue
	}

	public get doing(): DeepReadonly<RunWorkQueueItem> | undefined {
		if (this.doingIdx === -1) return undefined
		return this.queue[this.doingIdx]
	}

	public setDoing(
		state: TestStatus.Failed | TestStatus.Success | TestStatus.Partial
	): void {
		if (this.doing == null) return
		this.queue[this.doingIdx].state = state
	}

	public updateItem(item: RunWorkQueueItem): void {
		if (this.doing == null) return
		this.queue[this.doingIdx] = item
	}

	/**
	 * Insert a work item to the front of the queue, ignoring filtering and priority.
	 *
	 * Use this sparingly.
	 * @param item
	 */
	public unshift(item: RunWorkQueueItem): void {
		if (!item) return
		this.queue.unshift(item)
	}

	/**
	 * Enqueues an item in the work queue
	 * @param item The item to enqueue
	 * @param options Optional parameters
	 */
	public enqueue(
		item: RunWorkQueueItem | PageComponent | Page,
		options?: { page?: Page; depth?: number }
	): void {
		const { page, depth = 0 } = options || {}
		if (item == null) {
			return
		}

		// Skip if item is a PageComponent and is in the skip list or matches a selector to skip
		if (item instanceof PageComponent) {
			const isTypeSkipped = this.itemsToSkip.includes(item.type)
			const matchesSelector = this.matchesSelectorToSkip(item)
			const matchingSelectorString = this.findMatchingSelectorString(item)

			if (isTypeSkipped || matchesSelector) {
				// Create a unique key for this component on this page to avoid duplicate logging
				const logKey = `${page?.id ?? 'no-page'}:${item.id}`

				// Call the callback if provided and we haven't logged this component yet
				if (
					this.onComponentSkipped &&
					!this.loggedSkippedComponents.has(logKey)
				) {
					this.loggedSkippedComponents.add(logKey)
					// Prioritize selector match logging over type skip (more specific)
					if (matchesSelector) {
						this.onComponentSkipped(
							item,
							'selector',
							matchingSelectorString,
							page
						)
					} else if (isTypeSkipped) {
						this.onComponentSkipped(item, 'type', undefined, page)
					}
				}
				return
			}
		}

		// For tracking duplicate queue items
		let itemToCheck: RunWorkQueueItem | undefined = undefined

		if (item instanceof Page) {
			if (this._filterFn(item)) {
				const pageItem: RunWorkQueueItem = {
					id: item.id,
					type: RunWorkQueueItemType.Page,
					label: item.title,
					state: TestStatus.Todo,
					pageId: item.id,
				}
				this.itemDepths.set(pageItem.id, 0)

				itemToCheck = pageItem

				// Check if the page item already exists in the queue
				if (!this.isDuplicate(pageItem) && !this.onlyTestLinks) {
					this.queue.push(pageItem)
				}
			}
			const enqueue = this.enqueue.bind(this)
			this.lastEnqueuedPage = item
			this.itemDepths.clear() // Clear depth cache when page context changes
			this.itemDepths.set(item.id, 0)
			item.components.forEach(c =>
				enqueue(c, { page: item as Page, depth: 1 })
			)
		} else if (item instanceof PageComponent) {
			// If onlyTestLinks is true, skip non-Anchor components
			const isLink = item.type === 'Anchor'
			const shouldEnqueueComponent = this.onlyTestLinks ? isLink : true

			if (shouldEnqueueComponent && this._filterFn(item)) {
				const componentItem: RunWorkQueueItem = {
					id: item.id,
					type: RunWorkQueueItemType.Component,
					label: item.label,
					state: TestStatus.Todo,
					pageId: page?.id,
					componentId: item.id,
					item: {
						label: item.label,
						pageUrl: page?.url || '',
					},
				}
				this.itemDepths.set(componentItem.id, depth)

				itemToCheck = componentItem

				// Check if the component item already exists in the queue
				const isDup = this.isDuplicate(componentItem)

				if (!isDup) {
					this.queue.push(componentItem)
				}
			}

			const enqueue = this.enqueue.bind(this)

			// If this is a table and fastTestTables is true, only enqueue components from a random row
			if (this.fastTestTables && item.type === 'Table') {
				// If we've already processed this table, skip it
				if (this.processedTableIds.has(item.id)) {
					return
				}

				// Find and enqueue all header components
				const headers = item.components.filter(
					c =>
						c.type === 'Table Header' ||
						c.type === 'Table Header Cell' ||
						(c.type === 'Table Row' &&
							c.components.some(
								cell => cell.type === 'Table Header Cell'
							))
				)
				headers.forEach(header => enqueue(header, { page, depth }))

				const tableBody = item.components.find(
					c => c.type === 'Table Body'
				)
				if (!tableBody) {
					return
				}

				// Find all regular table rows (excluding header rows)
				const rows = tableBody.components.filter(
					c =>
						c.type === 'Table Row' &&
						c.components.every(
							cell => cell.type !== 'Table Header Cell'
						)
				)
				// Select and enqueue a random row if there are any rows
				if (rows.length > 0) {
					const randomIndex = Math.floor(Math.random() * rows.length)
					const randomRow = rows[randomIndex]
					randomRow.components.forEach(c =>
						enqueue(c, { page, depth })
					)
				}

				// Mark this table as processed
				this.processedTableIds.add(item.id)
			} else {
				// Only enqueue child components if this isn't a table component or if fastTestTables is false
				if (!this.fastTestTables || item.type !== 'Table') {
					item.components.forEach(c => enqueue(c, { page, depth }))
				}
			}
		} else {
			// If it's already a RunWorkQueueItem, check if it exists before adding
			itemToCheck = item

			if (!this.isDuplicate(itemToCheck)) {
				item.state = TestStatus.Todo
				this.itemDepths.set(item.id, depth)
				this.queue.push(item)
			}
		}

		// Sort the queue after enqueueing the item
		// and its children, if it has any
		const todo = this.queue.slice(this.doingIdx + 1)

		if (this.usePriority && this.priorityField) {
			// Priority-based sorting
			const sortedQueue = todo.sort((a, b) => {
				const aHistory = this.testHistory.get(a.id)
				const bHistory = this.testHistory.get(b.id)
				switch (this.priorityField) {
					case 'lastTested':
						// Prioritize least tested items (ascending order)
						if (!aHistory?.lastTested && !bHistory?.lastTested)
							return 0 // Both have no history
						if (!aHistory?.lastTested) return -1 // a is less tested (no history)
						if (!bHistory?.lastTested) return 1 // b is less tested (no history)

						// Compare based on lastTested date
						return (
							(aHistory?.lastTested as Date).getTime() -
							(bHistory?.lastTested as Date).getTime()
						)
					case 'lastResult':
						if (!aHistory?.lastResult && !bHistory?.lastResult)
							return 0 // Both have no history
						if (
							!aHistory?.lastResult &&
							bHistory?.lastResult === 'failed'
						)
							return 1
						if (
							!bHistory?.lastResult &&
							aHistory?.lastResult === 'failed'
						)
							return -1

						// Compare based on lastResult
						return aHistory?.lastResult === 'failed' ? -1 : 1
					case 'shallowest':
						return (
							this.calculateItemDepth(a) -
							this.calculateItemDepth(b)
						)
					case 'deepest':
						return (
							this.calculateItemDepth(b) -
							this.calculateItemDepth(a)
						)
					default:
						return 0
				}
			})
			this.queue.splice(this.doingIdx + 1, todo.length, ...sortedQueue)
		} else {
			// Use custom sort function provided
			todo.sort(this._sortFn)
			this.queue.splice(this.doingIdx + 1, todo.length, ...todo)
		}
	}

	/**
	 * Checks if an item is a duplicate of an existing item in the queue
	 * Uses multiple checks to determine if two items are likely the same
	 * @param item The item to check
	 * @returns true if the item is likely a duplicate
	 */
	private isDuplicate(item: RunWorkQueueItem): boolean {
		// Skip check for invalid items
		if (!item) return false

		// For form components specifically, we need multiple checks
		if (
			item.type === RunWorkQueueItemType.Component ||
			item.type === RunWorkQueueItemType.Form_Field
		) {
			for (const queueItem of this.queue) {
				// Skip items that aren't components or form fields
				if (
					queueItem.type !== RunWorkQueueItemType.Component &&
					queueItem.type !== RunWorkQueueItemType.Form_Field
				) {
					continue
				}

				// Skip items that are already completed, failed, or partial
				if (
					queueItem.state !== TestStatus.Todo &&
					queueItem.state !== TestStatus.Doing
				) {
					continue
				}

				// Check for exact ID match first (most reliable)
				if (queueItem.id === item.id) {
					return true
				}

				// Primary check: same componentId and pageId
				if (
					queueItem.componentId &&
					item.componentId &&
					queueItem.pageId &&
					item.pageId &&
					queueItem.componentId === item.componentId &&
					queueItem.pageId === item.pageId
				) {
					return true
				}

				// Secondary check: matching label and pageId
				if (
					queueItem.label &&
					item.label &&
					queueItem.pageId &&
					item.pageId &&
					queueItem.label === item.label &&
					queueItem.pageId === item.pageId
				) {
					// If both have component IDs and they are different, they are NOT duplicates
					if (
						queueItem.componentId &&
						item.componentId &&
						queueItem.componentId !== item.componentId
					) {
						continue
					}
					return true
				}

				// Additional check for forms with the same URL and similar labels
				if (
					queueItem.item?.pageUrl &&
					item.item?.pageUrl &&
					queueItem.item.pageUrl === item.item.pageUrl &&
					queueItem.label &&
					item.label &&
					queueItem.label.includes(item.label)
				) {
					return true
				}
			}

			// No duplicates found for this form
			return false
		}

		// For other item types, check ID and type
		return this.queue.some(
			queueItem =>
				queueItem.id === item.id &&
				queueItem.type === item.type &&
				(queueItem.state === TestStatus.Todo ||
					queueItem.state === TestStatus.Doing)
		)
	}

	/**
	 * Dequeues the next item from the work queue
	 * @returns The next item in the queue, or undefined if the queue is empty
	 */
	public dequeue(): DeepReadonly<RunWorkQueueItem> | undefined {
		const { queue, doingIdx } = this

		if (queue.length === 0) {
			this.processedTableIds.clear() // Clear when queue is empty
			return undefined
		}
		if (
			this.doingIdx < this.queue.length - 1 &&
			this.doing?.state == TestStatus.Doing
		) {
			return queue[doingIdx]
		}

		this.doingIdx++
		const next = doingIdx < queue.length ? queue[this.doingIdx] : undefined
		if (next != null) {
			next.state = TestStatus.Doing
		} else {
			// Clear processed table IDs when we've dequeued the last item
			this.processedTableIds.clear()
		}
		return next
	}

	/**
	 * Checks if the queue includes a specific application component
	 * @param modelItem The component to check for
	 * @returns true if the component is in the queue
	 */
	public includes(modelItem: ApplicationComponent): boolean {
		if (modelItem == null) return false

		if (modelItem.componentType === ApplicationComponentType.Page)
			return this.queue.some(
				item =>
					item.type === RunWorkQueueItemType.Page &&
					item?.pageId === modelItem.id
			)

		return this.queue.some(
			item =>
				item &&
				'componentId' in item &&
				item?.componentId === modelItem.id
		)
	}

	/**
	 * Checks if a component matches any of the selectors to skip
	 * @param component The component to check
	 * @returns True if the component matches any of the selectors to skip
	 */
	private matchesSelectorToSkip(component: PageComponent): boolean {
		if (!this.selectorsToSkip || this.selectorsToSkip.length === 0) {
			return false
		}

		// Check if any selector matches
		return this.selectorsToSkip.some(selector =>
			doesElementMatchSelector(selector, component, this.lastEnqueuedPage)
		)
	}

	/**
	 * Finds the selector string that matches the component
	 * @param component The component to check
	 * @returns The matching selector string, or undefined if no match
	 */
	private findMatchingSelectorString(
		component: PageComponent
	): string | undefined {
		if (!this.selectorsToSkip || this.selectorsToSkip.length === 0) {
			return undefined
		}

		// Find the first matching selector and return its corresponding string
		for (let i = 0; i < this.selectorsToSkip.length; i++) {
			const selector = this.selectorsToSkip[i]
			if (
				doesElementMatchSelector(
					selector,
					component,
					this.lastEnqueuedPage
				)
			) {
				// Return the corresponding string if available, otherwise return empty string
				// to indicate a match was found (for logging purposes)
				return i < this.selectorStringsToSkip.length
					? this.selectorStringsToSkip[i]
					: ''
			}
		}

		return undefined
	}

	/**
	 * Calculates the depth of a work queue item based on its component hierarchy
	 * @param item The work queue item to calculate depth for
	 * @returns The depth of the item (0 for root items, increasing for nested items)
	 */
	private calculateItemDepth(item: RunWorkQueueItem): number {
		// Check cache first
		if (this.itemDepths.has(item.id)) {
			return this.itemDepths.get(item.id) as number
		}

		// Page items are always at depth 0
		if (item.type === RunWorkQueueItemType.Page) return 0

		// Items without componentId are root components (depth 1)
		// This check might be too simplistic if an item can be a root component of a page but still have a componentId from its original definition.
		// However, based on current logic, componentId implies it's part of a page's component tree.
		if (!item.componentId) {
			this.itemDepths.set(item.id, 1)
			return 1
		}

		// Find the parent page component
		const parentPage = this.lastEnqueuedPage
		if (!parentPage) {
			this.itemDepths.set(item.id, 1) // Default to 1 if no parent page context
			return 1
		}

		// Find the component in the page hierarchy
		const findComponentDepth = (
			component: PageComponent,
			targetId: string,
			currentDepth: number = 1
		): number => {
			if (component.id === targetId) return currentDepth

			for (const child of component.components) {
				const depth = findComponentDepth(
					child,
					targetId,
					currentDepth + 1
				)
				if (depth !== -1) return depth
			}

			return -1
		}

		// Start search from the page's root components
		for (const component of parentPage.components) {
			const depth = findComponentDepth(component, item.componentId)
			if (depth !== -1) {
				this.itemDepths.set(item.id, depth)
				return depth
			}
		}

		// Default to depth 1 if component not found in the current page hierarchy
		this.itemDepths.set(item.id, 1)
		return 1
	}
}
